import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Building2, Loader2, Home } from "lucide-react";
import { Field } from "@/components/common";
import { Link } from "react-router-dom";

function fmtErr(d) {
  if (!d) return "Login failed. Please try again.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e.msg || JSON.stringify(e)).join(" ");
  return d.msg || String(d);
}

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try { await login(email, password); }
    catch (e2) { setErr(fmtErr(e2.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex flex-col justify-between w-1/2 bg-slate-950 text-slate-50 p-12">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-md bg-indigo-600 flex items-center justify-center"><Building2 className="w-5 h-5" /></div>
          <span className="font-heading font-bold text-xl">RentLedger</span>
        </div>
        <div>
          <h1 className="font-heading text-5xl font-bold tracking-tight leading-tight">Rental Property<br />Management &amp;<br /><span className="text-indigo-400">Accounts</span></h1>
          <p className="text-slate-400 mt-6 max-w-md">Properties, tenants, automatic rent invoicing, collections, expenses and financial reports — built for Indian landlords.</p>
        </div>
        <p className="text-slate-500 text-sm">© {new Date().getFullYear()} RentLedger. All rights reserved.</p>
      </div>
      <div className="flex-1 flex items-center justify-center p-6 bg-slate-50">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5" data-testid="login-form">
          <div className="lg:hidden flex items-center gap-2 mb-2">
            <div className="w-9 h-9 rounded-md bg-indigo-600 flex items-center justify-center"><Building2 className="w-5 h-5 text-white" /></div>
            <span className="font-heading font-bold text-xl">RentLedger</span>
          </div>
          <div>
            <h2 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Welcome back</h2>
            <p className="text-slate-500 text-sm mt-1">Sign in to manage your properties.</p>
          </div>
          <Field label="Email"><Input data-testid="login-email" type="email" autoComplete="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} required /></Field>
          <Field label="Password"><Input data-testid="login-password" type="password" autoComplete="current-password" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} required /></Field>
          {err && <p className="text-sm text-rose-600" data-testid="login-error">{err}</p>}
          <Button data-testid="login-submit" type="submit" disabled={loading} className="w-full bg-indigo-600 hover:bg-indigo-700">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign In"}
          </Button>
          <p className="text-sm text-slate-500 text-center">New to RentLedger? <Link to="/register" data-testid="go-register" className="text-indigo-600 font-medium">Create an account</Link></p>
          <div className="flex items-center gap-3 py-1"><div className="flex-1 h-px bg-slate-200" /><span className="text-xs text-slate-400">Paying rent?</span><div className="flex-1 h-px bg-slate-200" /></div>
          <Link to="/portal/login" data-testid="go-tenant-portal" className="w-full flex items-center justify-center gap-2 h-11 rounded-md border-2 border-emerald-200 bg-emerald-50 text-emerald-800 text-sm font-semibold hover:bg-emerald-100 transition-colors">
            <Home className="w-4 h-4" /> Sign in as a tenant (OTP or password)
          </Link>
        </form>
      </div>
    </div>
  );
}
